import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, MATERIAL_COLORS, MATERIALS, BENNE_SIZES_ALL, allowedBenneSizes } from "@/lib/apiClient";
import { Recycle, Coins, Mail } from "lucide-react";

const BENNE_SIZES = BENNE_SIZES_ALL;

export default function PublicPricing() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/pricing/${token}`);
        setData(data);
      } catch (e) {
        setError("Grille tarifaire introuvable ou expirée.");
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50 p-6 text-center">
        <div>
          <div className="font-heading font-black text-2xl mb-2">Oups</div>
          <p className="text-stone-500">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50 text-stone-400">
        Chargement…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 font-body text-stone-900">
      <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur grid place-items-center">
              <Recycle className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-black text-2xl sm:text-3xl tracking-tight">FB Environnement</div>
              <div className="text-emerald-100 text-sm font-bold uppercase tracking-[0.15em]">Grille tarifaire</div>
            </div>
          </div>
          <div className="mt-6 bg-white/10 backdrop-blur rounded-2xl p-4 inline-flex items-center gap-3">
            <Coins className="w-5 h-5" strokeWidth={2.5} />
            <div>
              <div className="font-bold text-sm">{data.commercial_name}</div>
              {data.commercial_email && (
                <div className="text-emerald-100 text-xs inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {data.commercial_email}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-white rounded-3xl border border-stone-100 p-5 sm:p-6 shadow-[0_4px_20px_rgb(0,0,0,0.04)] overflow-x-auto">
          <table className="min-w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="text-left py-3 pr-3 font-bold text-stone-700 sticky left-0 bg-white z-10">Matière</th>
                <th className="px-2 py-3 font-bold text-stone-700 text-center whitespace-nowrap">€/tonne</th>
                {BENNE_SIZES.map((s) => (
                  <th key={s} className="px-2 py-3 font-bold text-stone-700 text-center whitespace-nowrap">
                    {s} m³
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MATERIALS.map((m) => {
                const row = data.prices[m] || {};
                const ton = parseFloat(row.per_ton) || 0;
                return (
                  <tr key={m} className="border-t border-stone-100">
                    <td className="py-3 pr-3 sticky left-0 bg-white z-10">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${MATERIAL_COLORS[m] || "bg-stone-500 text-white"}`}>
                        {m}
                      </span>
                    </td>
                    <td className="px-2 py-3 text-center font-bold tabular-nums">
                      {ton > 0 ? `${ton} €` : <span className="text-stone-300">—</span>}
                    </td>
                    {BENNE_SIZES.map((s) => {
                      const allowed = allowedBenneSizes(m).includes(s);
                      const v = allowed ? (parseFloat(row.per_benne?.[s]) || 0) : 0;
                      return (
                        <td key={s} className="px-2 py-3 text-center tabular-nums">
                          {v > 0 ? `${v} €` : <span className="text-stone-300">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-center text-xs text-stone-400 mt-6">
          {"Tarifs indicatifs · Valables sous réserve d'acceptation · Édité par "}{data.commercial_name}
        </p>
      </div>
    </div>
  );
}
